import {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  Context,
  useCallback,
  useMemo,
  ReactNode,
  MutableRefObject,
} from 'react'

import styled from 'styled-components'

import { gaSendCustomEvent } from 'analytics'
import { useReferredState } from 'hooks/useReferredState'
import { getLanguage } from 'storage'

export type PlayerProgress = {
  current: number
  remaining: number
  percent: number
}

type AudioPlayerPositionContextType = {
  progress: PlayerProgress
  setProgress: (position: PlayerProgress) => void
}

export const AudioElement = styled.audio`
  height: 0;
  position: relative;
  visibility: hidden;
  width: 0;
  z-index: -1;
`

export const StoryEnded = new Event('story-ended')
export const StoryReset = new Event('story-reset')

export const AudioPlayerPositionContext =
  createContext<AudioPlayerPositionContextType>({
    progress: {
      current: 0,
      remaining: 0,
      percent: 0,
    },
    setProgress: () => {},
  })

type AudioPlayerContextType = {
  loaded: boolean
  error: boolean
  onSeek: (percent: number) => void
  onPlayButtonClick: () => void
  playing: boolean
  // pageState: number
  audioUrl: string | undefined
  audioStoryId: string | undefined
  setAudioUrl: (url: string | undefined) => void
  setAudioStoryId: (url: string | undefined) => void
  skip: (seconds: number) => void
  duration: number
  showPlayer: boolean
  setShowPlayer: (value: boolean) => void
  audioRef: MutableRefObject<HTMLAudioElement> | undefined
  playableStartSent: boolean
  // resetAudio: (allow: boolean) => void
}
export const AudioPlayerContext: Context<AudioPlayerContextType> =
  createContext<AudioPlayerContextType>({
    loaded: false,
    error: false,
    onSeek: () => {},
    onPlayButtonClick: () => {},
    playing: false,
    // pageState: 0,
    audioUrl: undefined,
    audioStoryId: undefined,
    setAudioUrl: () => {},
    setAudioStoryId: () => {},
    skip: () => {},
    duration: 0,
    showPlayer: false,
    setShowPlayer: () => {},
    audioRef: undefined,
    playableStartSent: false,
    // resetAudio: () => {},
  })

export function useAudioContext() {
  const context = useContext<AudioPlayerContextType>(AudioPlayerContext)
  if (!context) {
    throw new Error('useAudioContext must be used within a AudioProvider')
  }
  return context
}

export function useAudioPositionContext() {
  const context = useContext<AudioPlayerPositionContextType>(
    AudioPlayerPositionContext
  )
  if (!context) {
    throw new Error(
      'useAudioPositionContext must be used within a AudioProvider'
    )
  }
  return context
}

export const AudioProvider = (props: { children: ReactNode }) => {
  // The playable start event should only be sent the first time the audio is played.
  const [playableStartSent, setPlayableStartSent] = useState(false)
  const [audioUrl, setAudioUrl] = useState<string | undefined>(undefined)
  const [playing, setPlaying] = useState<boolean>(false)
  const [showPlayer, setShowPlayer] = useState<boolean>(false)
  const [audioStoryId, setAudioStoryId] = useReferredState<string | undefined>(
    undefined
  )
  const [error, setError] = useState(false)
  const [allowReset, setAllowReset] = useState<boolean>(false)

  const [audioLoaded, setAudioLoaded] = useState(false)

  const [loaded, setLoaded] = useState(false)

  const audioRef = useRef<HTMLAudioElement>(new Audio())

  const [progress, setProgress] = useState<PlayerProgress>({
    current: 0,
    remaining: 0,
    percent: 0,
  })

  const endPlay = () => {
    if (audioRef.current) {
      console.log(`send duration on end play ${audioRef.current.duration}`)
      gaSendCustomEvent(
        `stories/${audioStoryId.current}/${getLanguage()}/duration`,
        {
          duration: audioRef.current.duration,
        }
      )
      const audio = audioRef.current
      audioRef.current.currentTime = 0
      audio.pause()
      setPlayableStartSent(false)
      document.dispatchEvent(StoryEnded)
    }
  }

  const resetAudio = useCallback(() => {
    if (audioRef.current && allowReset) {
      console.log(
        `send duration on reset of item that has been partial played ${progress.current}`
      )
      gaSendCustomEvent(
        `stories/${audioStoryId.current}/${getLanguage()}/duration`,
        {
          duration: progress.current,
        }
      )
      const audio = audioRef.current
      audioRef.current.currentTime = 0
      audio.pause()
      setPlayableStartSent(false)
    }
  }, [allowReset, progress])

  const onSeek = (percent: number) => {
    if (audioRef.current) {
      const audio = audioRef.current

      audio.pause()

      const newPosition = percent * audio.duration

      audio.currentTime = newPosition

      const current = newPosition
      const remaining = audio.duration - current

      setProgress({
        current,
        remaining,
        percent: percent * 100,
      })

      if (playing) {
        audio.play()
      }
    }
  }

  const skip = (seconds: number) => {
    if (audioRef.current) {
      const audio = audioRef.current
      audio.pause()

      const seek = audio.currentTime || 0
      let newPosition = seek + seconds

      // Prevent Skipping beyond limits
      newPosition = newPosition < 0 ? 0 : newPosition
      newPosition = newPosition > audio.duration ? audio.duration : newPosition

      audio.currentTime = newPosition

      const current = newPosition
      const remaining = audio.duration - current
      const percent = (audio.currentTime / audio.duration) * 100

      setProgress({
        current,
        remaining,
        percent,
      })

      if (playing) {
        audio.play()
      }
    }
  }

  const step = () => {
    if (audioRef.current) {
      const audio = audioRef.current
      const seek = audio.currentTime || 0
      const current = seek
      const remaining = audio.duration - audio.currentTime
      const percent = (audio.currentTime / audio.duration) * 100

      setProgress({
        current,
        remaining,
        percent,
      })
      if (playing) {
        requestAnimationFrame(step.bind(this))
      }
    }
  }

  const onPlayButtonClick = () => {
    if (audioRef.current) {
      const audio = audioRef.current
      if (!playing) {
        audio.play()

        if (!playableStartSent) {
          setPlayableStartSent(true)
        }
      } else {
        audio.pause()
      }
    }
  }

  const progressPlay = () => {
    setPlaying(true)
  }

  const onPause = () => {
    setPlaying(false)
  }

  useEffect(() => {
    if (audioRef.current) {
      const audio = audioRef.current
      audio.addEventListener('ended', endPlay)
      audio.addEventListener('pause', onPause)
      audio.addEventListener('play', progressPlay)
    }
    return () => {
      if (audioRef.current) {
        const audio = audioRef.current
        audio.removeEventListener('ended', endPlay)
        audio.removeEventListener('pause', onPause)
        audio.removeEventListener('play', progressPlay)
      }
    }
  }, [audioRef.current])

  useEffect(() => {
    setAllowReset(!playing && playableStartSent)
    document.addEventListener('story-reset', resetAudio)
    return () => document.removeEventListener('story-reset', resetAudio)
  }, [playing, playableStartSent, allowReset, resetAudio])

  useEffect(() => {
    if (!audioUrl) {
      setShowPlayer(true)
    }
  }, [audioUrl])

  useEffect(() => {
    if (playing) step()
  }, [playing])

  const unload = () => {
    if (audioRef.current) {
      // audioRef.current.unload()
    }
  }

  const runErrorState = useCallback(() => {
    setError(true)
    unload()
  }, [audioRef.current])

  useEffect(() => {
    if (audioLoaded) {
      setLoaded(true)
    }
    step()
  }, [audioLoaded])

  useEffect(() => {
    setError(false)

    if (audioUrl) {
      const audio = audioRef.current
      audio.src = `${audioUrl}`
      audio.onloadeddata = () => {
        setAudioLoaded(true)
      }
      audio.onerror = () => {
        runErrorState()
      }
      audio.load()
    }
    return () => {
      unload()
      setPlaying(false)
      if (audioRef.current) {
        audioRef.current.currentTime = 0
        setProgress({
          current: 0,
          remaining: 0,
          percent: 0,
        })
      }
    }
  }, [audioUrl])

  const value = useMemo(() => {
    return {
      loaded,
      error,
      onSeek,
      onPlayButtonClick,
      playing,
      audioUrl,
      setAudioUrl,
      skip,
      audioStoryId: audioStoryId.current,
      setAudioStoryId,
      duration: audioRef?.current?.duration || 0,
      showPlayer,
      setShowPlayer,
      audioRef,
      playableStartSent,
    }
  }, [
    loaded,
    audioUrl,
    playing,
    error,
    audioStoryId,
    showPlayer,
    playableStartSent,
  ])

  const valueProgress = useMemo(() => {
    return {
      progress,
      setProgress,
    }
  }, [progress, setProgress])

  return (
    <AudioPlayerContext.Provider value={value}>
      <AudioElement ref={audioRef} />
      <AudioPlayerPositionContext.Provider value={valueProgress} {...props} />
    </AudioPlayerContext.Provider>
  )
}
